import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Interfaz genérica oficial para envolver respuestas de la API de Sakoo.
 */
export interface SakooResponse<T> {
  code: number;
  message?: string;
  track_code?: string;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class ApiGatewayService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  /**
   * Genera los encabezados necesarios para las peticiones de la API de Sakoo.
   * Agrega automáticamente el token de autorización si está disponible en sessionStorage.
   */
  private getHeaders(customHeaders?: HttpHeaders | { [header: string]: string | string[] }): HttpHeaders {
    let headers = customHeaders instanceof HttpHeaders 
      ? customHeaders 
      : new HttpHeaders(customHeaders || {});

    const token = sessionStorage.getItem('access_token');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  /**
   * Realiza una petición HTTP GET genérica.
   * @param path Ruta del endpoint
   * @param options Opciones de configuración del HttpClient (Headers, Query Params).
   * @returns Un Observable con el tipo de respuesta esperado.
   */
  get<T>(
    path: string,
    options?: {
      headers?: HttpHeaders | { [header: string]: string | string[] };
      params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> };
    }
  ): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http.get<T>(this.buildUrl(path), { ...options, headers });
  }

  /**
   * Realiza una petición HTTP POST genérica.
   * @param path Ruta del endpoint
   * @param body Cuerpo de la petición de tipo R.
   * @param options Opciones de configuración de la petición.
   * @returns Un Observable con el tipo de respuesta esperado.
   */
  post<T, R = any>(
    path: string,
    body: R,
    options?: {
      headers?: HttpHeaders | { [header: string]: string | string[] };
    }
  ): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http.post<T>(this.buildUrl(path), body, { ...options, headers });
  }

  /**
   * Realiza una petición HTTP PUT genérica.
   * @param path Ruta del endpoint
   * @param body Cuerpo de la petición de tipo R a actualizar.
   * @param options Opciones de configuración de la petición.
   * @returns Un Observable con el tipo de respuesta esperado.
   */
  put<T, R = any>(
    path: string,
    body: R,
    options?: {
      headers?: HttpHeaders | { [header: string]: string | string[] };
    }
  ): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http.put<T>(this.buildUrl(path), body, { ...options, headers });
  }

  /**
   * Realiza una petición HTTP DELETE genérica.
   * @param path Ruta del endpoint
   * @param options Opciones de configuración de la petición.
   * @returns Un Observable con el tipo de respuesta esperado.
   */
  delete<T>(
    path: string,
    options?: {
      headers?: HttpHeaders | { [header: string]: string | string[] };
    }
  ): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http.delete<T>(this.buildUrl(path), { ...options, headers });
  }

  /**
   * Método auxiliar para normalizar y construir la URL absoluta uniendo la base del entorno activo y la ruta.
   */
  private buildUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const cleanBase = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
    return `${cleanBase}/${cleanPath}`;
  }
}
